import { useCallback, useRef, useState } from 'react';
import { CanvasAction, CanvasActionExtra, triggerCanvasAction } from '../services/aiCanvasActionService';
import { NarrationControlRef } from '../components/aicanvas/NarrationControl';

export interface AppendBlockFn {
  (blockType: 'TEXT' | 'IMAGE' | 'CHART', attrs: Record<string, any>): void;
}

/**
 * Shared streaming orchestration for the guided AI actions (spec 044 US2, extended spec 046
 * US2/US4/US6 with CHECK_ANSWER/REVEAL_SOLUTION/IMAGE_QUESTION), reused by the tutor's
 * editable canvas and a student's GN (StudentGnPanel) — the append target and cue UI differ
 * per surface, but the block-streaming/narration/stop logic is identical.
 * <p>
 * A block is only committed to the canvas document once (via `appendBlock`) when it
 * completes — there is no "update an existing node's text" capability, so in-progress text
 * is tracked in `streamingText` for the host to render as a temporary preview, not written
 * into the document until `block_complete`. This avoids leaving empty/duplicate nodes behind.
 */
export function useCanvasActionStreaming(
  role: 'tutor' | 'student',
  courseId: number,
  nodeId: number,
  appendBlock: AppendBlockFn,
  narrationRef: React.RefObject<NarrationControlRef>
) {
  const [generating, setGenerating] = useState(false);
  const [streamingText, setStreamingText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastVerdict, setLastVerdict] = useState<'CORRECT' | 'INCORRECT' | 'PARTIAL' | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const currentTextRef = useRef('');

  const triggerAction = useCallback((action: CanvasAction, selectedText: string, extra?: CanvasActionExtra) => {
    setGenerating(true);
    setError(null);
    setStreamingText('');
    setLastVerdict(null);
    currentTextRef.current = '';

    abortRef.current = triggerCanvasAction(role, courseId, nodeId, action, selectedText, {
      onBlockStart: () => {
        currentTextRef.current = '';
        setStreamingText('');
      },
      onBlockChunk: (_blockId, text) => {
        currentTextRef.current += text;
        setStreamingText(currentTextRef.current);
      },
      onBlockComplete: (_blockId, _persistedBlockId, narration, blockType, attrs) => {
        if (blockType === 'TEXT') {
          appendBlock('TEXT', { text: currentTextRef.current || narration, origin: 'AI' });
        } else {
          appendBlock(blockType, { ...attrs, origin: 'AI' });
        }
        currentTextRef.current = '';
        setStreamingText('');
      },
      onNarrationReady: (_blockId, audioUrl) => {
        narrationRef.current?.play(audioUrl);
      },
      onDone: (verdict) => {
        setLastVerdict(verdict ?? null);
        setGenerating(false);
        setStreamingText(null);
        abortRef.current = null;
      },
      onError: (message) => {
        setError(message);
        setGenerating(false);
        setStreamingText(null);
        abortRef.current = null;
      },
    }, extra);
  }, [role, courseId, nodeId, appendBlock, narrationRef]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    narrationRef.current?.stop();
    setGenerating(false);
    setStreamingText(null);
  }, [narrationRef]);

  const clearVerdict = useCallback(() => setLastVerdict(null), []);

  return { triggerAction, stop, generating, streamingText, error, lastVerdict, clearVerdict };
}
